/**
 * Geometry of the liquid-glass morph windows (layout/MorphWindow.tsx): the
 * window's clip-path grows from the tapped round button to the whole screen
 * on a spring, and shrinks back into it on close.
 *
 * Pure functions, so the motion is unit-tested without a browser timeline.
 */

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Frame {
  width: number;
  height: number;
}

/** Damped harmonic spring: damping ratio and natural frequency (rad/s). */
export interface Spring {
  zeta: number;
  omega: number;
}

export const MORPH_OPEN_MS = 440;
export const MORPH_CLOSE_MS = 380;
/** Reduced motion: a plain crossfade, no morph. */
export const REDUCED_FADE_MS = 180;
/** Clip-path samples per animation: smooth at 60fps, cheap to build. */
export const MORPH_STEPS = 28;

/**
 * Open: the sides lead and overshoot slightly more than the top and bottom,
 * so the button swells into a soft drop before it settles as the screen.
 */
export const OPEN_SPRINGS = {
  horizontal: { zeta: 0.7, omega: 16 },
  vertical: { zeta: 0.78, omega: 14 },
} satisfies Record<string, Spring>;

/** Close: critically damped, quick, landing on the button without a wobble. */
export const CLOSE_SPRINGS = {
  horizontal: { zeta: 1, omega: 16 },
  vertical: { zeta: 1, omega: 18 },
} satisfies Record<string, Spring>;

/** Spring step response at `t` seconds: 0 at rest, 1 at its target. */
export const springProgress = (t: number, { zeta, omega }: Spring): number => {
  if (t <= 0) return 0;
  if (zeta >= 1) {
    return 1 - Math.exp(-omega * t) * (1 + omega * t);
  }
  const damped = omega * Math.sqrt(1 - zeta * zeta);
  return (
    1 -
    Math.exp(-zeta * omega * t) *
      (Math.cos(damped * t) + ((zeta * omega) / damped) * Math.sin(damped * t))
  );
};

const round = (value: number) => Math.round(value * 100) / 100;

/**
 * The window's clip, as an `inset()` of a frame-sized box, for progress `x`
 * (0 = the button, 1 = the whole frame) along the width and `y` along the
 * height. Values past 1 (the overshoot) push the edges outside the frame,
 * where they are simply off screen.
 */
export const morphClip = (
  trigger: Box,
  frame: Frame,
  x: number,
  y: number,
): string => {
  const top = trigger.top * (1 - y);
  const left = trigger.left * (1 - x);
  const right = (frame.width - trigger.left - trigger.width) * (1 - x);
  const bottom = (frame.height - trigger.top - trigger.height) * (1 - y);
  // Huge at first (clamped by the browser to a circle, then an ellipse, a
  // drop), easing into the screen's square edge as it lands.
  const blob = Math.max(frame.width, frame.height);
  const spread = Math.min(1, Math.max(0, Math.min(x, y)));
  const radius = Math.max(
    Math.min(trigger.width, trigger.height) / 2,
    blob * (1 - spread) ** 3,
  );
  const settled = spread >= 1 ? 0 : radius;
  return `inset(${round(top)}px ${round(right)}px ${round(bottom)}px ${round(
    left,
  )}px round ${round(settled)}px)`;
};

/**
 * Sampled clip-path keyframes (linear between samples, so the spring itself
 * is the easing). Opening runs from the button to the frame, closing from
 * the frame back to the button; both end exactly on their target.
 */
export const morphKeyframes = (
  trigger: Box,
  frame: Frame,
  direction: "open" | "close",
  steps = MORPH_STEPS,
): string[] => {
  const opening = direction === "open";
  const springs = opening ? OPEN_SPRINGS : CLOSE_SPRINGS;
  const seconds = (opening ? MORPH_OPEN_MS : MORPH_CLOSE_MS) / 1000;
  return Array.from({ length: steps + 1 }, (_, index) => {
    if (index === steps) {
      return opening
        ? morphClip(trigger, frame, 1, 1)
        : morphClip(trigger, frame, 0, 0);
    }
    const t = (index / steps) * seconds;
    const x = springProgress(t, springs.horizontal);
    const y = springProgress(t, springs.vertical);
    return opening
      ? morphClip(trigger, frame, x, y)
      : morphClip(trigger, frame, 1 - x, 1 - y);
  });
};

/** Centre of the trigger, as a transform-origin for the content zoom. */
export const triggerOrigin = (trigger: Box): string =>
  `${round(trigger.left + trigger.width / 2)}px ${round(
    trigger.top + trigger.height / 2,
  )}px`;
